import type { EChartsOption } from 'echarts';
import type { TooltipComponentFormatterCallbackParams } from 'echarts';
import type { ParsedTable, ChartGeneratorSettings } from '../types';
import {
  toNumber,
  axisPercentFormatter,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  chartLegend,
  columnDataLabel,
  CHART_GRID_TOP_WITH_LEGEND,
  CHART_GRID_BOTTOM,
  uniqueCategoryNames,
  cleanCategoryName,
} from './helpers';
import { filterTableData } from '../utils/chartFilters';
import { sortTableForChart } from '../utils/chartSort';

/**
 * Generates ECharts options for a vertical Clustered (Grouped) Column chart
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings (selectedRows, selectedColumns, dataType)
 * @returns ECharts option configuration object for a clustered column chart
 * @throws {GeneratorError} If filtering removes all data
 */
export function clusteredColumnOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  // Positions win over names (#1808); a set sort_order puts the bars in
  // reading order. Absent, table order as before.
  const sortOrder = settings?.sortOrder
  const pickedTable = filterTableData(
    t,
    settings?.selectedRows,
    settings?.selectedColumns,
    settings?.selectedRowPositions,
  )
  const filteredTable = sortOrder ? sortTableForChart(pickedTable, 'clusteredColumn', sortOrder) : pickedTable

  const xAxisNames = filteredTable.headers.slice(1) // Column labels
  // Repeated labels stay separate columns (#1808); unique ones are unchanged.
  const xAxisData = uniqueCategoryNames(xAxisNames)
  const seriesNames = filteredTable.rows.map((row) => row[0] || 'Unnamed')

  // Check if we should color by x-axis category (when single series and colorMap matches categories)
  const colorByCategory = settings?.colorMap && seriesNames.length === 1 &&
    xAxisNames.some(cat => settings.colorMap![cat.toLowerCase()])

  // Check if gradientMap is available
  const gradientMap = settings?.gradientMap

  // #1201: opt-in percentage labels above each column. Off unless the graph
  // config asks for them — see columnDataLabel.
  const dataLabel = columnDataLabel(settings)

  const series: echarts.BarSeriesOption[] = filteredTable.rows.map((row, rowIndex) => {
    const data = xAxisNames.map((category, colIndex) => {
      const dataIndex = colIndex + 1
      const value = dataIndex < row.length ? toNumber(row[dataIndex]) : null

      // If coloring by category, return object with itemStyle
      if (colorByCategory) {
        const key = category.toLowerCase();
        // Prefer gradient if available, then colorMap, then index-based fallback
        const gradient = gradientMap?.[key];
        const color = settings?.colorMap?.[key] || getChartColorByIndex(colIndex);

        return {
          value,
          itemStyle: { color: gradient || color },
        }
      }
      return value
    })

    // For non-category coloring, apply color based on series name
    const seriesKey = seriesNames[rowIndex].toLowerCase();
    const seriesColor = settings?.colorMap?.[seriesKey] || getChartColorByIndex(rowIndex);

    return {
      name: seriesNames[rowIndex],
      type: 'bar',
      data: data,
      emphasis: {
        focus: 'series',
      },
      ...(dataLabel ? { label: dataLabel } : {}),
      ...(!colorByCategory ? { itemStyle: { color: seriesColor } } : {}),
    }
  })

  const hasData = series.some((s) => s.data?.some((d) => {
    if (d === null) return false
    // Handle both plain numbers and {value, itemStyle} objects
    const val = typeof d === 'object' && d !== null && 'value' in d ? (d as any).value : d
    return val !== null && !isNaN(val as number)
  }))
  if (!hasData) {
    throw new GeneratorError('No valid numeric data found after filtering.')
  }

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (!Array.isArray(params)) return ''
        // #1201: with ONE series the series name says nothing the reader does
        // not already have — Sky's emotion chart plots a single count column
        // across emotions, so the old three-line tooltip read
        // "Anger / [swatch] count(*): 34.0%". Collapse it to `Anger: 34.0%`.
        // Multi-series charts keep the per-series rows, where the name is the
        // whole point of the tooltip.
        if (params.length === 1) {
          const only = params[0]
          const onlyValue = typeof only.value === 'number' ? only.value : null
          const onlyFormatted =
            onlyValue !== null
              ? settings?.dataType === 'freq'
                ? onlyValue.toLocaleString()
                : `${(onlyValue * 100).toFixed(1)}%`
              : 'N/A'
          return `${cleanCategoryName(only.name)}: ${onlyFormatted}`
        }
        let tooltipText = `${cleanCategoryName(params[0].name)}<br/>`
        params.forEach((item) => {
          const value = typeof item.value === 'number' ? item.value : null
          const formattedValue =
            value !== null
              ? settings?.dataType === 'freq'
                ? value.toLocaleString()
                : `${(value * 100).toFixed(1)}%`
              : 'N/A'
          const color = (item.color as string) || '#ccc'
          tooltipText += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${item.seriesName}: ${formattedValue}<br/>`
        })
        return tooltipText
      },
    },
    legend: chartLegend(seriesNames),
    grid: {
      left: '3%',
      right: '4%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      bottom: CHART_GRID_BOTTOM,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        rotate: 30,
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
    },
    series: series,
    color: resolveSeriesColors(seriesNames, settings?.colorMap),
  }
}
